import {environments} from '../../app.json'

export type Environment = 'development' | 'production' | 'localhost'
type Target = 'app' | 'embed'
type BuiltInVariant = 'live' | 'stage' | 'unstable'
export type Variant = string

const variantPaths: Record<BuiltInVariant, {appDir: string; embedDir: string}> = {
    live: {
        appDir: 'new',
        embedDir: 'app',
    },
    stage: {
        appDir: 'stage/new',
        embedDir: 'stage/app',
    },
    unstable: {
        appDir: 'unstable/new',
        embedDir: 'unstable/app',
    },
}

export interface AppEnvironment {
    anticsUrl: string
    apiHost: string
    apiMode: Environment
    apiVersion: string
    devServerIp?: string
    target: Target
    staticEmbedURL: string | null
    staticEmbedScriptURL: string | null
    staticLocaleURL: string | null
    staticSiteURL: string | null
    siteSecret?: string
    variant?: Variant
    xUserAgent: string
    isNative: boolean
}

const target: Target = process.env.TARGET as Target
const isNative = process.env.PLATFORM === 'native'
const isWeb = process.env.PLATFORM === 'web'
const subdir = process.env.SUBDIR

let siteSecret: string = process.env.SITE_SECRET as string
let xUserAgent: string = process.env.X_USER_AGENT as string
let variant: Variant = process.env.VARIANT as Variant
let mode: Environment = process.env.MODE as Environment
let apiMode: Environment = (process.env.API || mode) as Environment
let devServerIp: string = process.env.DEV_SERVER_IP as string
let isProduction = mode === 'production'

const getVariantPathsFor = (selectedVariant: Variant) => {
    if (subdir) {
        return {
            appDir: `${subdir}/new`,
            embedDir: `${subdir}/app`,
        }
    }
    if (selectedVariant === 'stage') {
        return variantPaths.stage
    }
    if (selectedVariant === 'unstable') {
        return variantPaths.unstable
    }
    if (selectedVariant && selectedVariant !== 'live') {
        return {
            appDir: `${selectedVariant}/new`,
            embedDir: `${selectedVariant}/app`,
        }
    }
    return variantPaths.live
}
const getVariantPaths = () => getVariantPathsFor(variant)
const dir = () => getVariantPaths().appDir
const embedDir = () => getVariantPaths().embedDir

const {
    apiHost,
    apiVersion,
} = environments[apiMode] ?? {}

const staticLocaleURL = () => (isWeb ?
    isProduction ? `https://ninchat.com/${dir()}` : `${target === 'embed' ? 'https://localhost:8082' : 'http://localhost:8080'}`
    :
    isNative ?
        isProduction ? `https://ninchat.com/${dir()}` : `https://${devServerIp}:8082`
        :
        null)

const staticSiteURL = () => (isNative ?
    isProduction ? `https://ninchat.com/${dir()}` : `https://${devServerIp}:8082`
    :
    isProduction ? `https://ninchat.com/${dir()}` : 'https://localhost:8082')

const staticEmbedURL = () => (isNative ?
    isProduction ? `https://ninchat.com/${embedDir()}` : `https://${devServerIp}:8082`
    :
    isProduction ? `https://ninchat.com/${embedDir()}` : 'https://localhost:8082')

const staticEmbedScriptURL = () => (isNative ?
    isProduction ? `https://ninchat.com/${dir()}` : `https://${devServerIp}:8083`
    :
    isProduction ? `https://ninchat.com/${dir()}` : 'https://localhost:8083')


const environment: AppEnvironment = {
    anticsUrl: `https://antics.${apiHost.replace('api.', '').replace('api6.', '')}`,
    apiHost,
    apiMode,
    apiVersion,
    devServerIp,
    ...siteSecret && apiMode !== 'production' ? {siteSecret} : {},
    isNative,
    staticEmbedScriptURL: staticEmbedScriptURL(),
    staticEmbedURL: staticEmbedURL(),
    staticLocaleURL: staticLocaleURL(),
    staticSiteURL: staticSiteURL(),
    target,
    variant,
    xUserAgent,
}

const initialEnvironment = {...environment}

// Embed instances must not share the mutable environment used by the hosted app.
export const resolveInstanceEnvironment = (overrides: {
    apiMode?: Environment
    devServerIp?: string
    mode?: Environment
    variant?: Variant
} = {}): AppEnvironment => {
    const selectedMode = overrides.mode || process.env.MODE as Environment
    const selectedApiMode = overrides.apiMode || overrides.mode || (process.env.API || process.env.MODE) as Environment
    const selectedVariant = overrides.variant || process.env.VARIANT as Variant
    const selectedDevServerIp = overrides.devServerIp || process.env.DEV_SERVER_IP
    const selectedApi = environments[selectedApiMode] || {}
    const {
        appDir, embedDir: selectedEmbedDir,
    } = getVariantPathsFor(selectedVariant)
    const production = selectedMode === 'production'
    const localSiteURL = isNative ? `https://${selectedDevServerIp}:8082` : 'https://localhost:8082'
    const siteURL = production ? `https://ninchat.com/${appDir}` : localSiteURL

    return {
        ...initialEnvironment,
        anticsUrl: `https://antics.${selectedApi.apiHost.replace('api.', '').replace('api6.', '')}`,
        apiHost: selectedApi.apiHost,
        apiMode: selectedApiMode,
        apiVersion: selectedApi.apiVersion,
        devServerIp: selectedDevServerIp,
        staticEmbedScriptURL: production ? `https://ninchat.com/${appDir}` : isNative ? `https://${selectedDevServerIp}:8083` : 'https://localhost:8083',
        staticEmbedURL: production ? `https://ninchat.com/${selectedEmbedDir}` : localSiteURL,
        staticLocaleURL: isWeb ? production ? siteURL : target === 'embed' ? 'https://localhost:8082' : 'http://localhost:8080' : isNative ? siteURL : null,
        staticSiteURL: siteURL,
        variant: selectedVariant,
    }
}

export const setEnvironment = (env: {
    apiMode?: Environment
    mode?: Environment
    variant?: Variant
    devServerIp?: string
    siteSecret?: string
    xUserAgent?: string
}) => {
    const {
        apiMode: apiModeOverride, variant: variantOverride, devServerIp: devServerIPOverride,
        siteSecret: siteSecretOverride, mode: modeOverride, xUserAgent: xUserAgentOverride,
    } = env

    if (modeOverride) {
        mode = modeOverride
        isProduction = modeOverride === 'production'
    }

    if (apiModeOverride) {
        apiMode = apiModeOverride
        environment.apiMode = apiModeOverride
        if (apiModeOverride !== 'production' && siteSecret) {
            environment.siteSecret = siteSecret
        } else {
            environment.siteSecret = undefined
        }
    }

    if (variantOverride) {
        variant = variantOverride
        environment.variant = variantOverride
    }

    if (devServerIPOverride) {
        devServerIp = devServerIPOverride
        environment.devServerIp = devServerIp
    }

    if (xUserAgentOverride) {
        xUserAgent = xUserAgentOverride
        environment.xUserAgent = xUserAgent
    }

    if (siteSecretOverride) {
        if (apiMode !== 'production' && siteSecret) {
            siteSecret = siteSecretOverride
            environment.siteSecret = siteSecret
        } else {
            siteSecret = ''
            environment.siteSecret = undefined
        }
    }

    environment.staticLocaleURL = staticLocaleURL()
    environment.staticSiteURL = staticSiteURL()
    environment.staticEmbedURL = staticEmbedURL()
    environment.staticEmbedScriptURL = staticEmbedScriptURL()
}

export const getEnvironment = (): AppEnvironment => environment
